import { useEffect } from 'react';

const SITE_ORIGIN = 'https://www.indemniza.pe';
const DEFAULT_SOCIAL_IMAGE = '/og-indemniza.webp';
const DEFAULT_SOCIAL_IMAGE_ALT = 'Corporación Indemniza';

type Meta = {
  title: string;
  description: string;
  canonicalPath?: string;
  image?: string;
  imageAlt?: string;
  robots?: 'index,follow' | 'noindex,nofollow';
};

type MetaAttribute = 'name' | 'property';

function upsertMeta(attribute: MetaAttribute, key: string, content: string) {
  let tag = document.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);

  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attribute, key);
    document.head.appendChild(tag);
  }

  tag.content = content;
}

function removeMeta(attribute: MetaAttribute, key: string) {
  document.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`)?.remove();
}

function setCanonical(href?: string) {
  const links = Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel="canonical"]'));

  if (!href) {
    links.forEach((link) => link.remove());
    return;
  }

  const [canonical, ...duplicates] = links;
  const tag = canonical ?? document.createElement('link');
  tag.rel = 'canonical';
  tag.href = href;

  if (!canonical) {
    document.head.appendChild(tag);
  }

  duplicates.forEach((link) => link.remove());
}

export function useDocumentMeta({
  title,
  description,
  canonicalPath,
  image = DEFAULT_SOCIAL_IMAGE,
  imageAlt = DEFAULT_SOCIAL_IMAGE_ALT,
  robots = 'index,follow',
}: Meta) {
  useEffect(() => {
    const canonicalUrl = canonicalPath
      ? new URL(canonicalPath, `${SITE_ORIGIN}/`).toString()
      : undefined;
    const imageUrl = image ? new URL(image, `${SITE_ORIGIN}/`).toString() : undefined;

    document.title = title;
    upsertMeta('name', 'description', description);
    upsertMeta('name', 'robots', robots);

    upsertMeta('property', 'og:type', 'website');
    upsertMeta('property', 'og:site_name', 'Corporación Indemniza');
    upsertMeta('property', 'og:locale', 'es_PE');
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);

    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);

    setCanonical(canonicalUrl);

    if (canonicalUrl) {
      upsertMeta('property', 'og:url', canonicalUrl);
    } else {
      removeMeta('property', 'og:url');
    }

    if (imageUrl) {
      upsertMeta('property', 'og:image', imageUrl);
      upsertMeta('property', 'og:image:alt', imageAlt);
      upsertMeta('name', 'twitter:image', imageUrl);
      upsertMeta('name', 'twitter:image:alt', imageAlt);
    } else {
      removeMeta('property', 'og:image');
      removeMeta('property', 'og:image:alt');
      removeMeta('name', 'twitter:image');
      removeMeta('name', 'twitter:image:alt');
    }
  }, [canonicalPath, description, image, imageAlt, robots, title]);
}
